import React, { useState, useEffect } from 'react';
import { Edit2, Save, X } from 'lucide-react';
import { showSuccessMessage, showAxiosError } from '../../../../utils/toast';
import { bookEntryService } from '../../work_space/services/book_entry_service';

export const BookEntry = ({ ordersList, initialOrderId, lead }) => {
  const safeOrdersList = Array.isArray(ordersList) ? ordersList.filter(o => o.status !== 'SAVED') : [];
  const [selectedOrderId, setSelectedOrderId] = useState(initialOrderId || (safeOrdersList.length > 0 ? safeOrdersList[0]._id : null));
  const order = safeOrdersList.find(o => o._id === selectedOrderId) || safeOrdersList[0] || null;

  const [isSubmitted, setIsSubmitted] = useState(false);
  const orderIdStr = order?._id ? `SO-${order._id.substring(order._id.length - 6).toUpperCase()}` : 'SO-1035';
  
  const calculateGrandTotal = (currentOrder) => {
    if (!currentOrder || !currentOrder.items) return 0;
    const subtotal = currentOrder.items.reduce((sum, item) => {
      const qty = Number(item.qty) || 1;
      const rate = Number(item.item_details?.price || item.item_details?.purchaseRate || item.item_details?.rate || 0);
      return sum + (qty * rate);
    }, 0);

    const discountType = currentOrder.discount_type || '%';
    const discountValue = currentOrder.discount_value || 0;
    // Handles both '%' (saved by estimation) and 'percentage' (legacy)
    const discountAmount = (discountType === '%' || discountType === 'percentage')
      ? Math.round(subtotal * (discountValue / 100))
      : Number(discountValue);
    const taxableValue = subtotal - discountAmount;
    const gstValue = currentOrder.gst_value || 0;
    const gstAmount = Math.round(taxableValue * (gstValue / 100));
    return taxableValue + gstAmount;
  };

  const computedGrandTotal = order?.amount || order?.grandTotal || calculateGrandTotal(order);

  const [formData, setFormData] = useState({
    customerInfo: lead?.name || lead?.customerName || '',
    grandTotal: computedGrandTotal || '',
    materialAmount: '',
    transport: '',
    carpenterAmount: '',
    iron: '',
    hardware: '',
    puPaints: '',
    designAmount: '',
    isUPVC: false,
    upvcAmount: '',
    notes: ''
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (order?._id) {
      fetchBookEntry();
    }
  }, [order?._id]);

  const fetchBookEntry = async () => {
    try {
      const res = await bookEntryService.getBookEntryByEstimation(order._id);
      if (res && res.data) {
        const entry = res.data;
        setFormData({
          customerInfo: lead?.name || lead?.customerName || '',
          grandTotal: entry.grand_total || computedGrandTotal || '',
          materialAmount: entry.material || '',
          transport: entry.transport || '',
          carpenterAmount: entry.carpenter || '',
          iron: entry.iron || '',
          hardware: entry.hardware || '',
          puPaints: entry.pu_paints || '',
          designAmount: entry.design || '',
          isUPVC: entry.is_upvc || false,
          upvcAmount: entry.upvc || '',
          notes: entry.notes || ''
        });
        setIsSubmitted(true);
      } else {
        // Reset if no entry found for the newly selected order
        setFormData({
          customerInfo: lead?.name || lead?.customerName || '',
          grandTotal: computedGrandTotal || '',
          materialAmount: '',
          transport: '',
          carpenterAmount: '',
          iron: '',
          hardware: '',
          puPaints: '',
          designAmount: '',
          isUPVC: false,
          upvcAmount: '',
          notes: ''
        });
        setIsSubmitted(false);
      }
    } catch (err) {
      console.error(err);
      // Fallback reset on error
      setFormData({
        customerInfo: lead?.name || lead?.customerName || '',
        grandTotal: computedGrandTotal || '',
        materialAmount: '',
        transport: '',
        carpenterAmount: '',
        iron: '',
        hardware: '',
        puPaints: '',
        designAmount: '',
        isUPVC: false,
        upvcAmount: '',
        notes: ''
      });
      setIsSubmitted(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type !== 'checkbox' && name !== 'customerInfo' && name !== 'notes') {
      // Allow empty string to clear the input
      if (value !== '') {
        const numValue = Number(value);
        if (isNaN(numValue) || numValue > 100000000) return;
      }
    }
    
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSave = async () => {
    if (!formData.customerInfo || !formData.grandTotal) {
       return; 
    }
    
    setIsLoading(true);
    try {
      await bookEntryService.createOrUpdateBookEntry({
        estimation_id: order._id,
        customer_id: lead._id,
        grand_total: Number(formData.grandTotal) || 0,
        material: Number(formData.materialAmount) || 0,
        transport: Number(formData.transport) || 0,
        carpenter: Number(formData.carpenterAmount) || 0,
        iron: Number(formData.iron) || 0,
        hardware: Number(formData.hardware) || 0,
        pu_paints: Number(formData.puPaints) || 0,
        design: Number(formData.designAmount) || 0,
        is_upvc: formData.isUPVC || false,
        upvc: Number(formData.upvcAmount) || 0,
        notes: formData.notes
      });
      setIsSubmitted(true);
      showSuccessMessage('Book entry saved successfully!');
    } catch (err) {
      showAxiosError(err, 'Failed to save book entry');
    } finally {
      setIsLoading(false);
    }
  };

  // Shared classes
  const labelClass = "block text-[12px] text-slate-700 mb-1";
  const inputClass = "w-full px-3 py-1.5 border border-slate-300 rounded-md outline-none focus:border-slate-500 focus:ring-1 focus:ring-slate-500 bg-white text-[13px]";
  const viewClass = "w-full px-3 py-1.5 border border-transparent bg-slate-50 rounded-md text-[13px] text-slate-800";

  const carpenterAndIronTotal = 
    (Number(formData.carpenterAmount) || 0) + 
    (Number(formData.iron) || 0);

  const othersTotal = 
    (Number(formData.transport) || 0) + 
    (Number(formData.hardware) || 0) + 
    (Number(formData.puPaints) || 0) + 
    (Number(formData.designAmount) || 0);

  const upvcTotal = Number(formData.upvcAmount) || 0;
  const materialTotal = Number(formData.materialAmount) || 0;
  const grandTotalValue = Number(formData.grandTotal) || 0;

  const sumOfAllEntered = materialTotal + carpenterAndIronTotal + othersTotal + (formData.isUPVC ? upvcTotal : 0);
  const isNotEqualTotal = sumOfAllEntered !== grandTotalValue;

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-4 flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3 w-full md:max-w-lg">
          <span className="text-sm font-semibold text-slate-700 whitespace-nowrap">Select Order:</span>
          <select 
            className="w-full border border-slate-300 rounded-md text-sm px-3 py-1.5 focus:outline-none focus:border-black bg-slate-50 text-slate-700 font-medium cursor-pointer"
            value={selectedOrderId || ''}
            onChange={(e) => setSelectedOrderId(e.target.value)}
          >
            {safeOrdersList.length === 0 && <option value="" disabled>No Orders Found</option>}
            {safeOrdersList.map((est, idx) => {
              const estNum = safeOrdersList.length - idx;
              const dateStr = new Date(est.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
              return (
                <option key={est._id} value={est._id}>
                  Order #{estNum} — {dateStr} ({est.status.replace(/_/g, ' ')})
                </option>
              );
            })}
          </select>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm w-full">
      {/* Summary Cards */}
      <div className={`grid grid-cols-1 md:grid-cols-${formData.isUPVC ? '5' : '4'} gap-4 mb-6`}>
        <div className="p-4 rounded-xl border border-slate-200 bg-slate-50 flex flex-col justify-center">
          <span className="text-[12px] font-medium text-slate-500 mb-1">Total Amount</span>
          <span className={`text-xl font-bold ${isNotEqualTotal ? 'text-red-500' : 'text-slate-800'}`}>₹{grandTotalValue.toLocaleString('en-IN')}</span>
        </div>
        <div className="p-4 rounded-xl border border-emerald-100 bg-emerald-50/50 flex flex-col justify-center">
          <span className="text-[12px] font-medium text-emerald-600 mb-1">Material Amount</span>
          <span className="text-xl font-bold text-emerald-700">₹{materialTotal.toLocaleString('en-IN')}</span>
        </div>
        <div className="p-4 rounded-xl border border-blue-100 bg-blue-50 flex flex-col justify-center">
          <span className="text-[12px] font-medium text-blue-600 mb-1">Carpenter + Iron</span>
          <span className="text-xl font-bold text-blue-700">₹{carpenterAndIronTotal.toLocaleString('en-IN')}</span>
        </div>
        <div className="p-4 rounded-xl border border-brand-orange/20 bg-brand-orange/5 flex flex-col justify-center">
          <span className="text-[11px] font-medium text-brand-orange mb-1">Others (Tr, Hw, PU, De)</span>
          <span className="text-xl font-bold text-brand-dark">₹{othersTotal.toLocaleString('en-IN')}</span>
        </div>
        {formData.isUPVC && (
          <div className="p-4 rounded-xl border border-purple-100 bg-purple-50 flex flex-col justify-center">
            <span className="text-[12px] font-medium text-purple-600 mb-1">UPVC Amount</span>
            <span className="text-xl font-bold text-purple-700">₹{upvcTotal.toLocaleString('en-IN')}</span>
          </div>
        )}
      </div>
      
      {isNotEqualTotal && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 text-red-600 text-[13px] rounded-lg">
          <strong>Warning:</strong> The sum of entered amounts (₹{sumOfAllEntered.toLocaleString('en-IN')}) is {sumOfAllEntered > grandTotalValue ? `₹${(sumOfAllEntered - grandTotalValue).toLocaleString('en-IN')} more` : `₹${(grandTotalValue - sumOfAllEntered).toLocaleString('en-IN')} less`} than the Total Amount (₹{grandTotalValue.toLocaleString('en-IN')}).
        </div>
      )}

      <div className="space-y-4">
        {/* Top Section */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>Customer Info <span className="text-red-500">*</span></label>
            {!isSubmitted ? (
                <input
                  type="text"
                  name="customerInfo"
                  value={formData.customerInfo}
                  disabled
                  className={`${inputClass} bg-slate-100 text-slate-500 cursor-not-allowed`}
                />
            ) : (
              <div className={viewClass}>{formData.customerInfo || '—'}</div>
            )}
          </div>
          <div>
            <div className="flex items-center justify-between mb-1">
              <label className={labelClass} style={{marginBottom: 0}}>Total Amount <span className="text-red-500">*</span></label>
              {!isSubmitted && (
                <label className="flex items-center gap-1.5 cursor-pointer text-[12px] text-slate-600 hover:text-brand-blue transition-colors">
                  <input
                    type="checkbox"
                    name="isUPVC"
                    checked={formData.isUPVC}
                    onChange={handleInputChange}
                    className="rounded border-slate-300 text-brand-blue focus:ring-brand-blue"
                  />
                  <span>UPVC Options</span>
                </label>
              )}
            </div>
            {!isSubmitted ? (
                <input
                  type="text"
                  name="grandTotal"
                  value={formData.grandTotal}
                  onChange={formData.isUPVC ? handleInputChange : undefined}
                  disabled={!formData.isUPVC}
                  className={`${inputClass} ${!formData.isUPVC ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`}
                />
            ) : (
              <div className="flex items-center justify-between">
                <div className={viewClass}>{formData.grandTotal || '—'}</div>
                {formData.isUPVC && <span className="text-[11px] font-medium text-brand-blue ml-2 shrink-0 bg-blue-50 px-2 py-1 rounded">UPVC</span>}
              </div>
            )}
          </div>
        </div>

        {/* Booking Details Section */}
        <div>
          <h3 className="text-[14px] text-slate-800 mb-3 mt-1">Booking Details</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className={labelClass}>Material</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="materialAmount"
                  value={formData.materialAmount}
                  onChange={handleInputChange}
                  placeholder="Enter material amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.materialAmount || '—'}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>Transport</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="transport"
                  value={formData.transport}
                  onChange={handleInputChange}
                  placeholder="Enter transport amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.transport || '—'}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>Carpenter</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="carpenterAmount"
                  value={formData.carpenterAmount}
                  onChange={handleInputChange}
                  placeholder="Enter carpenter amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.carpenterAmount || '—'}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>Iron</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="iron"
                  value={formData.iron}
                  onChange={handleInputChange}
                  placeholder="Enter iron amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.iron || '—'}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>Hardware</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="hardware"
                  value={formData.hardware}
                  onChange={handleInputChange}
                  placeholder="Enter hardware amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.hardware || '—'}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>PU Paints</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="puPaints"
                  value={formData.puPaints}
                  onChange={handleInputChange}
                  placeholder="Enter PU paints amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.puPaints || '—'}</div>
              )}
            </div>
            <div>
              <label className={labelClass}>Design</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="designAmount"
                  value={formData.designAmount}
                  onChange={handleInputChange}
                  placeholder="Enter design amount"
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.designAmount || '—'}</div>
              )}
            </div>

            {formData.isUPVC && (
              <div>
                <label className={labelClass}>UPVC Amount</label>
                {!isSubmitted ? (
                  <input
                    type="text"
                    name="upvcAmount"
                    value={formData.upvcAmount}
                    onChange={handleInputChange}
                    placeholder="Enter UPVC amount"
                    className={`${inputClass} border-purple-200 focus:border-purple-400 focus:ring-purple-400`}
                  />
                ) : (
                  <div className={`${viewClass} bg-purple-50`}>{formData.upvcAmount || '—'}</div>
                )}
              </div>
            )}

            <div className={formData.isUPVC ? "md:col-span-1" : "md:col-span-2"}>
              <label className={labelClass}>Notes</label>
              {!isSubmitted ? (
                <input
                  type="text"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  placeholder="Enter notes..."
                  className={inputClass}
                />
              ) : (
                <div className={viewClass}>{formData.notes || '—'}</div>
              )}
            </div>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t border-slate-100 gap-3">
          {/* isSubmitted && (
             <button
                onClick={() => setIsSubmitted(false)}
                className="flex items-center gap-2 px-5 py-2.5 bg-slate-100 text-slate-700 hover:bg-slate-200 rounded-lg text-[13px] font-medium transition-colors"
             >
                <Edit2 size={16} /> Edit Entry
             </button>
          ) */}
          {!isSubmitted && (
            <button
              onClick={handleSave}
              disabled={isLoading || isNotEqualTotal}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#0f172a] text-white hover:bg-black rounded-lg text-[13px] font-medium transition-colors disabled:opacity-50"
            >
              <Save size={16} /> {isLoading ? 'Saving...' : 'Save Book Entry'}
            </button>
          )}
        </div>
      </div>
    </div>
    </div>
  );
};
